import React, { useMemo, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { useQuery } from '@apollo/react-hooks';
import Content from 'views/components/ui/content';
import { GET_CLINIC_DT_CLINICIAN_CAPACITY } from 'api/graphql/v2/queries/Ops';
import { IOpsClinic, IOpsClinician, IOpsClinicianRow } from 'model/ops';
import {
  WEEKDAY_LABELS,
  buildClinicianRows
} from 'utils/mappers/response/clinicDtClinicians';
import { DISCIPLINE_COLOURS } from './disciplineColours';
import { OpsHeader } from './OpsHeader';
import { ClinicOpsWrapper, CrumbsWrapper } from './style';

const NUMERIC_COLUMNS = [
  'Staff',
  'Total sessions',
  'Booked sessions',
  'Open sessions'
];

/** The discipline column, the four figures, and Monday to Friday. */
const COLUMN_COUNT = 1 + NUMERIC_COLUMNS.length + WEEKDAY_LABELS.length;

/** Readable weekday segment for the day grid's URL. */
const WEEKDAY_SLUGS = ['mon', 'tue', 'wed', 'thu', 'fri'];

/** A zero is muted so a column of them does not read as the loudest thing. */
const Figure: React.FC<{ value: number }> = ({ value }) =>
  value === 0 ? <span className="zero">0</span> : <>{value}</>;

/**
 * What a weekday figure narrows the day grid to: exactly what that figure is
 * about, at whatever depth it sits.
 *
 * A clinician's figure is about one person, so it names them by id — there is
 * no other way to say "this person". The rows above are about a group, so they
 * name the group rather than the ids that happen to make it up today: a link to
 * `specialty=COTA` still means the COTAs here after someone joins or leaves,
 * where a frozen list would quietly drop them.
 */
const weekdayFilterFor = (row: IOpsClinicianRow) => {
  if (row.providerId) return `clinicians=${row.providerId}`;
  if (row.specialty) return `specialty=${row.specialty}`;
  return `discipline=${row.discipline}`;
};

const RowLabel: React.FC<{ row: IOpsClinicianRow }> = ({ row }) => {
  if (row.depth === 2) {
    return (
      <>
        {/* A profile opens in a new tab so a scheduler checking a template
            does not lose their place in the clinic. */}
        <a
          className="clinician-name"
          href={`/providers/${row.providerId}`}
          target="_blank"
          rel="noopener noreferrer"
        >
          {row.label}
        </a>
        {/* Only when there is one — an empty sublabel would still occupy its
            line and make that row taller than its neighbours. */}
        {!!row.sublabel && <span className="sublabel">{row.sublabel}</span>}
        {row.isUnconfigured && (
          <span className="unconfigured">No care sessions on their template</span>
        )}
        {row.hasNoBookedData && (
          <span className="unconfigured">
            No booked sessions calculated — openings may be overstated
          </span>
        )}
      </>
    );
  }
  return (
    <span className="discipline-chip">
      <span
        className="swatch"
        style={{ background: DISCIPLINE_COLOURS[row.discipline] }}
      />
      {row.label}
    </span>
  );
};

export const ClinicOpsTable: React.FC<{
  clinicId: string;
  clinicName: string;
  clinicians: IOpsClinician[];
}> = ({ clinicId, clinicName, clinicians }) => {
  const [expanded, setExpanded] = useState<string[]>([]);

  const rows = useMemo(() => buildClinicianRows(clinicians), [clinicians]);

  const toggle = (discipline: string) =>
    setExpanded(current =>
      current.includes(discipline)
        ? current.filter(item => item !== discipline)
        : [...current, discipline]
    );

  const visible = rows.filter(
    row => row.depth === 0 || expanded.includes(row.discipline)
  );

  return (
    <>
      <OpsHeader />

      {/* Beneath the section header the depth is carried by a breadcrumb rather
          than a second headline, so the table still sits high in the viewport
          and the clinic's name stays a step down from "Operations". */}
      <CrumbsWrapper>
        <nav aria-label="Breadcrumb">
          <Link to="/ops">Clinics</Link>
          <span className="divider" aria-hidden="true">
            /
          </span>
          <span aria-current="page">{clinicName}</span>
        </nav>
      </CrumbsWrapper>

      <ClinicOpsWrapper>
        <div className="table-card">
          <div className="table-scroll">
            <table>
              <thead>
                <tr>
                  <th scope="col">Discipline</th>
                  {NUMERIC_COLUMNS.map(column => (
                    <th key={column} scope="col" className="numeric">
                      {column}
                    </th>
                  ))}
                  {WEEKDAY_LABELS.map(day => (
                    <th key={day} scope="col" className="numeric day">
                      {day}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {rows.length === 0 && (
                  <tr>
                    <td className="empty-state" colSpan={COLUMN_COUNT}>
                      No DT clinicians at {clinicName}.
                    </td>
                  </tr>
                )}
                {visible.map(row => {
                  const isOpen = expanded.includes(row.discipline);
                  return (
                    <tr key={row.key} className={`depth-${row.depth}`}>
                      <td>
                        {row.depth === 0 ? (
                          <button
                            type="button"
                            className="twist-button"
                            aria-expanded={isOpen}
                            onClick={() => toggle(row.discipline)}
                          >
                            {/* Empty on purpose: the chevron is drawn in CSS
                                from the button's aria-expanded, so there is one
                                arrow and it cannot disagree with the state. */}
                            <span className="twist" aria-hidden="true" />
                            <RowLabel row={row} />
                          </button>
                        ) : (
                          <RowLabel row={row} />
                        )}
                      </td>
                      <td className="numeric">
                        {/* A person is not a headcount, so clinician rows
                            leave Staff empty rather than printing 1. */}
                        {row.staff === undefined ? (
                          <span
                            className="zero"
                            role="img"
                            aria-label="Not applicable"
                          >
                            —
                          </span>
                        ) : (
                          <Figure value={row.staff} />
                        )}
                      </td>
                      <td className="numeric">
                        <Figure value={row.totalSessions} />
                      </td>
                      <td className="numeric">
                        <Figure value={row.bookedSessions} />
                      </td>
                      <td className="numeric strong">
                        <Figure value={row.openSessions} />
                      </td>
                      {row.weekdayOpen.map((open, index) => (
                        <td
                          key={WEEKDAY_LABELS[index]}
                          className="numeric day strong"
                        >
                          {/* Visibly interactive rather than hover-only, and it
                              narrows the grid to what this figure is about —
                              the row's grade, or its discipline — rather than
                              opening the whole clinic. By name, not by the ids
                              behind it, so the link still means the same thing
                              after someone joins or leaves. Everyone else stays
                              one click away in the grid's own picker. */}
                          <Link
                            className="weekday-link"
                            to={`/ops/${clinicId}/${
                              WEEKDAY_SLUGS[index]
                            }?${weekdayFilterFor(row)}`}
                            title={`${open} open on ${WEEKDAY_LABELS[index]}s — open the grid for ${row.label}`}
                          >
                            <Figure value={open} />
                          </Link>
                        </td>
                      ))}
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
        <p className="table-footnote">
          Open sessions are care sessions on the active default template with no
          client in them. Where a weekly session limit is set, Total and Open follow
          that limit and the weekday figures show the template; otherwise the
          five weekday figures add up to the total. A
          clinician booked past their own template has no openings rather than
          negative values.
        </p>
      </ClinicOpsWrapper>
    </>
  );
};

const ClinicOpsPage: React.FC<{}> = () => {
  const { clinicId } = useParams<{ clinicId: string }>();
  const id = Number(clinicId);
  const { loading, error, data } = useQuery(GET_CLINIC_DT_CLINICIAN_CAPACITY, {
    variables: { clinicId: id },
    fetchPolicy: 'network-only'
  });

  return (
    <Content loading={loading} error={error} data={data}>
      {({
        clinics,
        clinicDtClinicianCapacity
      }: {
        clinics: IOpsClinic[];
        clinicDtClinicianCapacity: IOpsClinician[];
      }) => (
        <ClinicOpsTable
          clinicId={clinicId}
          // A clinic that has since been removed still reads as a place rather
          // than as a blank breadcrumb.
          clinicName={
            (clinics || []).find(clinic => clinic.id === id)?.name ||
            'This clinic'
          }
          clinicians={clinicDtClinicianCapacity}
        />
      )}
    </Content>
  );
};

export default ClinicOpsPage;
